import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Mail, Save, Send, Server, KeyRound, ShieldCheck, Zap, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = {
  provider: "resend",
  sender_email: "",
  sender_name: "",
  resend_api_key: "",
  resend_api_key_set: false,
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  smtp_password_set: false,
  smtp_use_tls: true,
};

// Prefill helpers for common domain-email hosts
const SMTP_PRESETS = [
  { id: "ionos", label: "IONOS", host: "smtp.ionos.com", port: 465, use_tls: true, note: "Port 465 (SSL). Username = full mailbox address, password = mailbox password." },
  { id: "dreamhost", label: "DreamHost", host: "smtp.dreamhost.com", port: 465, use_tls: true, note: "Port 465 (SSL). Username = full mailbox address." },
  { id: "gmail", label: "Gmail / Workspace", host: "smtp.gmail.com", port: 587, use_tls: true, note: "Use an App Password, not your account password." },
  { id: "m365", label: "Microsoft 365", host: "smtp.office365.com", port: 587, use_tls: true, note: "Requires SMTP AUTH enabled on the mailbox." },
  { id: "zoho", label: "Zoho Mail", host: "smtp.zoho.com", port: 587, use_tls: true, note: "Enable SMTP in Zoho Mail settings first." },
  { id: "fastmail", label: "Fastmail", host: "smtp.fastmail.com", port: 465, use_tls: true, note: "Use an app-specific password." },
  { id: "icloud", label: "iCloud+ Custom Domain", host: "smtp.mail.me.com", port: 587, use_tls: true, note: "Requires app-specific password." },
];

export default function EmailSettingsCard({ primaryColor = "#0D9488" }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [lastTest, setLastTest] = useState(null); // {ok, sender_email, provider, error?}

  // Pre-fill the recipient with the admin's own email so they can one-tap
  // send a test to themselves — matches Resend's "test mode" restriction.
  useEffect(() => {
    if (user?.email && !testRecipient) setTestRecipient(user.email);
  }, [user, testRecipient]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setCfg({ ...empty, ...data });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        provider: cfg.provider,
        sender_email: cfg.sender_email.trim(),
        sender_name: cfg.sender_name.trim(),
        smtp_host: cfg.smtp_host.trim(),
        smtp_port: Number(cfg.smtp_port) || 587,
        smtp_username: cfg.smtp_username.trim(),
        smtp_use_tls: cfg.smtp_use_tls,
      };
      // Only include secrets if the admin actually typed a new value
      if (cfg.resend_api_key && cfg.resend_api_key !== "***") payload.resend_api_key = cfg.resend_api_key;
      if (cfg.smtp_password && cfg.smtp_password !== "***") payload.smtp_password = cfg.smtp_password;
      const { data } = await api.patch("/admin/email-settings", payload);
      setCfg({ ...empty, ...data });
      toast.success("Email settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!testRecipient.trim()) {
      toast.error("Enter a recipient email for the test");
      return;
    }
    setTesting(true);
    setLastTest(null);
    try {
      const { data } = await api.post("/admin/email-settings/test", { recipient: testRecipient.trim() });
      toast.success(`Test sent from ${data.sender_email || "the configured sender"} ✓`);
      setLastTest({ ok: true, sender_email: data.sender_email, provider: data.provider, recipient: testRecipient.trim() });
    } catch (err) {
      const msg = formatApiError(err);
      toast.error(msg);
      setLastTest({ ok: false, error: msg, recipient: testRecipient.trim() });
    } finally {
      setTesting(false);
    }
  };

  const isResend = cfg.provider === "resend";
  const isSmtp = cfg.provider === "smtp";

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="email-settings-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: primaryColor }}>
          <Mail className="w-4 h-4 text-black" />
        </div>
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Delivery</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">Email Settings</div>
        </div>
        <div className="ml-auto text-[10px] font-mono-num uppercase tracking-widest text-slate-500">
          Used for password resets & future notifications
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 font-mono-num uppercase tracking-widest">Loading…</div>
      ) : (
        <form onSubmit={onSave} className="space-y-5" data-testid="email-settings-form">
          {/* Provider selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => set("provider", "resend")}
              className={`border p-4 text-left transition-colors ${isResend ? "border-[color:var(--brand,#0D9488)] bg-slate-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
              data-testid="email-provider-resend"
            >
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4" style={{ color: isResend ? primaryColor : "#64748B" }} />
                <div className="font-display font-black uppercase tracking-tight text-sm">Resend</div>
              </div>
              <div className="text-xs text-slate-500 leading-snug">
                Managed HTTP API. Verify a domain for full delivery.
              </div>
            </button>
            <button
              type="button"
              onClick={() => set("provider", "smtp")}
              className={`border p-4 text-left transition-colors ${isSmtp ? "border-[color:var(--brand,#0D9488)] bg-slate-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
              data-testid="email-provider-smtp"
            >
              <div className="flex items-center gap-2 mb-1">
                <Server className="w-4 h-4" style={{ color: isSmtp ? primaryColor : "#64748B" }} />
                <div className="font-display font-black uppercase tracking-tight text-sm">SMTP</div>
              </div>
              <div className="text-xs text-slate-500 leading-snug">
                Use your own mailbox (Gmail/Workspace, M365, cPanel, Zoho, etc.).
              </div>
            </button>
          </div>

          {/* Resend domain-verification tip */}
          {isResend && (
            <div className="border border-teal-900/60 bg-teal-950/30 p-3" data-testid="email-resend-domain-tip">
              <div className="font-mono-num text-[10px] uppercase tracking-widest text-teal-400 mb-1 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> // Domain verification
              </div>
              <div className="text-xs text-slate-800 leading-relaxed">
                By default, Resend only lets you send to the account owner's email address. To deliver dose reminders + weekly digests to <em>all</em> your users:
              </div>
              <ol className="text-xs text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                <li>Verify a domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[color:var(--brand,#0D9488)] underline underline-offset-2" data-testid="email-resend-domain-link">resend.com/domains</a> (add DNS TXT + CNAME records).</li>
                <li>Once verified, set <b>Sender Email</b> below to an address on that domain (e.g. <code className="text-teal-300">no-reply@yourdomain.com</code>).</li>
                <li>Save. Digests will start reaching every opted-in user on the next Sunday 09:00 UTC run.</li>
              </ol>
            </div>
          )}

          {/* Common: sender identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Sender Email</Label>
              <Input
                type="email"
                value={cfg.sender_email}
                onChange={(e) => set("sender_email", e.target.value)}
                placeholder={isResend ? "noreply@yourdomain.com" : "you@yourdomain.com"}
                className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                data-testid="email-sender-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Sender Name (optional)</Label>
              <Input
                value={cfg.sender_name}
                onChange={(e) => set("sender_name", e.target.value)}
                placeholder="The Amino Exchange"
                className="bg-slate-50 border-slate-200 rounded-none h-10"
                data-testid="email-sender-name"
              />
            </div>
          </div>

          {/* Resend fields */}
          {isResend && (
            <div className="space-y-3 pt-2 border-t border-slate-200">
              <div className="border border-amber-500/40 bg-amber-500/[0.06] p-3">
                <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1">// Heads up · Resend domain verification</div>
                <p className="text-xs text-slate-800 leading-relaxed">
                  In test mode Resend only lets you email <span className="text-slate-900 font-mono-num">the account owner's address</span>. To send password resets + weekly digests to <em>every</em> user, verify your domain at{" "}
                  <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-[#0D9488] hover:underline font-mono-num">resend.com/domains</a>
                  {" "}(adds SPF + DKIM in ~5 min). Then set the sender email above to an address on that verified domain.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                  <KeyRound className="w-3 h-3" /> Resend API Key
                  {cfg.resend_api_key_set && (
                    <span className="ml-2 text-[9px] font-mono-num tracking-widest text-emerald-400 border border-emerald-800 px-1.5 py-0.5">
                      SAVED
                    </span>
                  )}
                </Label>
                <Input
                  type="password"
                  value={cfg.resend_api_key}
                  onChange={(e) => set("resend_api_key", e.target.value)}
                  placeholder={cfg.resend_api_key_set ? "Leave blank to keep the saved key" : "re_..."}
                  className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                  data-testid="email-resend-api-key"
                />
                <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
                  Get one at resend.com → API Keys. Verify a domain before switching sender to your own address.
                </div>
              </div>
            </div>
          )}

          {/* SMTP fields */}
          {isSmtp && (
            <div className="space-y-3 pt-2 border-t border-slate-200">
              {/* Provider presets */}
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                  <Zap className="w-3 h-3" /> Quick presets
                </Label>
                <div className="flex flex-wrap gap-1.5" data-testid="smtp-presets">
                  {SMTP_PRESETS.map((p) => {
                    const active = cfg.smtp_host === p.host && Number(cfg.smtp_port) === p.port;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          set("smtp_host", p.host);
                          set("smtp_port", p.port);
                          set("smtp_use_tls", p.use_tls);
                          toast.success(`${p.label}: ${p.note}`);
                        }}
                        className={`text-[10px] font-mono-num uppercase tracking-widest px-2.5 h-7 border transition-colors ${
                          active
                            ? "border-[color:var(--brand,#0D9488)] bg-slate-50 text-[color:var(--brand,#0D9488)]"
                            : "border-slate-200 bg-slate-50 hover:border-zinc-600 text-slate-600 hover:text-slate-900"
                        }`}
                        data-testid={`smtp-preset-${p.id}`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">SMTP Host</Label>
                  <Input
                    value={cfg.smtp_host}
                    onChange={(e) => set("smtp_host", e.target.value)}
                    placeholder="smtp.dreamhost.com"
                    className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                    data-testid="email-smtp-host"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Port</Label>
                  <Input
                    type="number"
                    value={cfg.smtp_port}
                    onChange={(e) => set("smtp_port", e.target.value)}
                    placeholder="465"
                    className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                    data-testid="email-smtp-port"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Username</Label>
                  <Input
                    value={cfg.smtp_username}
                    onChange={(e) => set("smtp_username", e.target.value)}
                    placeholder="noreply@yourdomain.com"
                    className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                    data-testid="email-smtp-username"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                    Password
                    {cfg.smtp_password_set && (
                      <span className="ml-2 text-[9px] font-mono-num tracking-widest text-emerald-400 border border-emerald-800 px-1.5 py-0.5">
                        SAVED
                      </span>
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={cfg.smtp_password}
                    onChange={(e) => set("smtp_password", e.target.value)}
                    placeholder={cfg.smtp_password_set ? "Leave blank to keep saved password" : "Mailbox password"}
                    className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
                    data-testid="email-smtp-password"
                  />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={cfg.smtp_use_tls}
                  onChange={(e) => set("smtp_use_tls", e.target.checked)}
                  className="accent-[color:var(--brand,#0D9488)]"
                  data-testid="email-smtp-use-tls"
                />
                <span className="font-mono-num text-[11px] uppercase tracking-widest">Use STARTTLS (recommended on port 587). Port 465 auto-upgrades to implicit TLS.</span>
              </label>

              {/* DreamHost-specific setup guidance */}
              <details className="border border-slate-200 bg-slate-50" data-testid="smtp-help-dreamhost">
                <summary className="cursor-pointer px-3 py-2 text-[11px] font-mono-num uppercase tracking-widest text-slate-600 hover:text-slate-900 flex items-center gap-2">
                  <HelpCircle className="w-3 h-3" /> IONOS / DreamHost setup checklist (click to expand)
                </summary>
                <div className="px-4 py-3 border-t border-slate-200 text-xs text-slate-600 leading-relaxed space-y-3">
                  <div>
                    <span className="text-slate-900 font-semibold">For IONOS mailboxes:</span>
                    <div className="mt-1 grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 font-mono-num">
                      <span className="text-slate-500">SMTP Host</span><span className="text-slate-900">smtp.ionos.com</span>
                      <span className="text-slate-500">Port</span><span className="text-slate-900">465 (SSL) or 587 (STARTTLS)</span>
                      <span className="text-slate-500">Username</span><span className="text-slate-900">full mailbox address</span>
                      <span className="text-slate-500">Password</span><span className="text-slate-900">mailbox password (create in IONOS panel → Email)</span>
                      <span className="text-slate-500">SPF record</span><span className="text-[color:var(--brand,#0D9488)]">v=spf1 include:_spf-us.ionos.com ~all</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-900 font-semibold">For DreamHost mailboxes:</span>
                    <div className="mt-1 grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 font-mono-num">
                      <span className="text-slate-500">SMTP Host</span><span className="text-slate-900">smtp.dreamhost.com</span>
                      <span className="text-slate-500">Port</span><span className="text-slate-900">465 (SSL) or 587 (STARTTLS)</span>
                      <span className="text-slate-500">Username</span><span className="text-slate-900">full mailbox address</span>
                      <span className="text-slate-500">SPF record</span><span className="text-[color:var(--brand,#0D9488)]">v=spf1 include:netblocks.dreamhost.com ~all</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-900 font-semibold">DKIM & DMARC (both providers):</span>
                    <div className="text-slate-600 mt-1">Enable DKIM signing in the provider's email dashboard — it auto-generates a DKIM TXT record for your domain. Then add a DMARC record: <code className="text-[color:var(--brand,#0D9488)]">v=DMARC1; p=none; rua=mailto:you@yourdomain.com</code></div>
                  </div>
                </div>
              </details>

              <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest leading-relaxed">
                Tip: click a preset above to pre-fill the host/port/TLS for your provider, then just enter mailbox + password.
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-3 border-t border-slate-200">
            {/* Last test result banner — persists after toast fades so admins
                can trace what sender/provider was used. */}
            {lastTest && (
              <div
                className={`rounded-md border px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${
                  lastTest.ok
                    ? "bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-900/20 dark:text-teal-200"
                    : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:text-red-200"
                }`}
                data-testid="email-test-result"
              >
                <span className="font-semibold">{lastTest.ok ? "✓ Delivered" : "✕ Failed"}</span>
                <span className="text-slate-500 dark:text-slate-400">→ {lastTest.recipient}</span>
                {lastTest.ok && lastTest.sender_email && (
                  <span className="font-mono-num text-[11px]">from <b>{lastTest.sender_email}</b> · {lastTest.provider}</span>
                )}
                {!lastTest.ok && lastTest.error && (
                  <span className="font-mono-num text-[11px] leading-snug w-full mt-1">{lastTest.error}</span>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex-1 flex flex-wrap items-center gap-2">
                <Input
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="test-recipient@example.com"
                  className="bg-slate-50 border-slate-200 rounded-none h-10 max-w-xs font-mono-num"
                  data-testid="email-test-recipient"
                />
                {user?.email && testRecipient !== user.email && (
                  <button
                    type="button"
                    onClick={() => setTestRecipient(user.email)}
                    className="text-[10px] font-mono-num uppercase tracking-widest text-teal-700 hover:text-teal-900 dark:text-teal-300 whitespace-nowrap"
                    data-testid="email-test-send-to-me"
                  >
                    Send to me →
                  </button>
                )}
                <Button
                  type="button"
                  onClick={onTest}
                  disabled={testing}
                  className="h-10 rounded-none bg-slate-50 hover:bg-slate-200 border border-slate-200 text-slate-900 uppercase tracking-widest text-xs font-semibold"
                  data-testid="email-test-send"
                >
                  <Send className="w-3.5 h-3.5 mr-2" /> {testing ? "Sending…" : "Send test"}
                </Button>
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="h-11 px-6 rounded-none text-black uppercase tracking-widest font-semibold"
                style={{ backgroundColor: primaryColor }}
                data-testid="email-settings-save"
              >
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Email Settings"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Card>
  );
}
